<script lang="ts" setup>
import { computed, watch, ref, onUnmounted } from "vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import PlusIcon from "@magnit/icons/src/assets/icons/plus-24.svg";
import MinusIcon from "@magnit/icons/src/assets/icons/minus-24.svg";
import PlusIconSmall from "@magnit/icons/src/assets/icons/plus-16.svg";
import MinusIconSmall from "@magnit/icons/src/assets/icons/minus-16.svg";
import { type IStepperEmits, type IStepperProps } from "./VStepper.types";

const props = withDefaults(defineProps<IStepperProps>(), {
  size: "s",
  min: 0,
  max: 100,
  unit: "шт",
  w: "auto",
  durationCancel: 3000,
});
const emit = defineEmits<IStepperEmits>();

const isShowCancelButton = ref(false);
const cancelTimeout = ref<ReturnType<typeof setTimeout>>();

const isXSmall = computed(() => {
  return props.size === "xs";
});

const isMaximum = computed(() => {
  return props.value >= props.max;
});

const isMinimum = computed(() => {
  return props.value <= props.min;
});

const rootClass = computed(() => ({
  [`pl-stepper_size-${props.size}`]: props.size,
  "pl-stepper_fill": isXSmall.value && !isShowCancelButton.value,
}));

const onDecrement = () => {
  if (!isMinimum.value) {
    emit("decrement");
  }
};

const onIncrement = () => {
  if (!isMaximum.value) {
    emit("increment");
  }
};

const onCancel = () => {
  emit("cancel");
};

watch(
  () => props.value,
  (value): void => {
    if (!props.cancelable || value !== props.min) {
      return;
    }

    isShowCancelButton.value = true;
    cancelTimeout.value = setTimeout(() => {
      isShowCancelButton.value = false;
      emit("delete");
      clearTimeout(cancelTimeout.value);
    }, props.durationCancel);
  },
);

const counterFont = computed(() => {
  switch (props.size) {
    case "xs":
      return "caption-large-accent-low";
    case "m":
      return "body-large-accent-low";
    default:
      return "body-small-accent-low";
  }
});

const buttonSize = computed(() => {
  switch (props.size) {
    case "xs":
      return "s";
    case "m":
      return "l";
    default:
      return "s";
  }
});

onUnmounted(() => {
  clearTimeout(cancelTimeout.value);
});
</script>

<template>
  <div
    class="pl-stepper"
    :class="rootClass"
    :style="{
      width: typeof props.w === 'number' ? `${props.w}px` : props.w,
    }"
  >
    <VButton
      v-if="isShowCancelButton"
      :size="buttonSize"
      theme="secondary"
      @click="onCancel"
    >
      Отменить
    </VButton>
    <template v-else>
      <VIcon
        v-if="isXSmall"
        data-testid="VStepper-button"
        class="pl-stepper__icon-button"
        :class="{
          disabled: isMinimum,
        }"
        @click="onDecrement"
      >
        <MinusIconSmall />
      </VIcon>
      <VButton
        v-else
        data-testid="VStepper-button"
        :disabled="isMinimum"
        :size="buttonSize"
        theme="secondary"
        @click="onDecrement"
      >
        <template #icon>
          <VIcon color="inherit">
            <MinusIcon />
          </VIcon>
        </template>
      </VButton>

      <div class="pl-stepper__content">
        <VText :font="counterFont" tag="div" class="pl-stepper__counter">
          {{ props.value ?? 0 }}
        </VText>
        <VText
          color="secondary"
          font="caption-small-regular-low"
          tag="div"
          class="pl-stepper__unit"
        >
          {{ props.unit }}
        </VText>
      </div>

      <VIcon
        v-if="isXSmall"
        data-testid="VStepper-button"
        class="pl-stepper__icon-button"
        :class="{
          disabled: isMaximum,
        }"
        @click="onIncrement"
      >
        <PlusIconSmall />
      </VIcon>
      <VButton
        v-else
        data-testid="VStepper-button"
        :disabled="isMaximum"
        :size="buttonSize"
        theme="secondary"
        @click="onIncrement"
      >
        <template #icon>
          <VIcon color="inherit">
            <PlusIcon />
          </VIcon>
        </template>
      </VButton>
    </template>
  </div>
</template>

<style scoped lang="postcss">
.pl-stepper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  gap: var(--pl-unit-x2);

  &__content {
    user-select: none;
    padding: 0 var(--pl-unit-x1);
  }

  &_fill {
    background-color: var(--pl-button-background-secondary-default);
    border-radius: var(--pl-button-radius-s);
    padding: 0 var(--pl-unit-x1);
    height: var(--pl-unit-x8);

    .pl-stepper__unit {
      margin-top: -4px;
    }
  }

  &__icon-button {
    cursor: pointer;
    width: var(--pl-unit-x4);
    height: var(--pl-unit-x4);
    border-radius: var(--pl-unit-x1);
    color: var(--pl-text-primary);
    background-color: var(--pl-button-background-secondary-default);

    &:hover {
      background-color: var(--pl-button-background-secondary-hover);
    }

    &:active {
      background-color: var(--pl-button-background-secondary-pressed);
    }

    &.disabled {
      &,
      &:hover,
      &:focus {
        cursor: not-allowed;
        color: var(--pl-text-tertiary);
        background-color: var(--pl-button-background-secondary-default);
      }
    }
  }

  &__icon-wrapper {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }
}
</style>
